<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface TabItem {
    icon?: IconDefinition;
    title: string;
    id?: string | number;
    disabled?: boolean;
}

interface IProps {
    items?: TabItem[];
    type?: 'blue' | 'yellow' | 'white';
    fullWidth?: boolean;
}

const { items = [], type = 'blue', fullWidth } = defineProps<IProps>();

const modelValue = defineModel<string | number>({ default: 0 });

const computedItems = computed<(TabItem & { color: string })[]>(() =>
    items.map((item, index) => ({
        ...item,
        color: color(item, index),
    }))
);

function color(tab: TabItem, index: number): string {
    switch (type) {
        case 'blue':
            if (modelValue.value === (tab.id || index)) {
                return 'var(--blue-400)';
            } else {
                return 'var(--cool-gray-800)';
            }
        case 'yellow':
            if (modelValue.value === (tab.id || index)) {
                return 'black';
            } else {
                return 'var(--yellow-700)';
            }
    }
    return 'var(--cool-gray-800)';
}
</script>

<template>
    <div class="tabs" :class="[type]">
        <div
            v-for="(tab, index) in computedItems"
            :key="tab.id || index"
            class="tab"
            :class="{
                active: modelValue === (tab.id || index),
                full: fullWidth,
            }"
            @click="!tab.disabled && (modelValue = tab.id || index)"
        >
            <mr-icon
                v-if="tab.icon"
                :icon="tab.icon"
                class="icon"
                :color="tab.color"
            />
            {{ tab.title }}
        </div>
    </div>
</template>
<style lang="scss" scoped>
.tabs {
    display: flex;
    .tab {
        cursor: pointer;
        border-bottom-width: 3px;
        border-bottom-style: solid;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 51px;
        padding-left: 16px;
        padding-right: 16px;
        white-space: nowrap;
        &.full {
            flex-grow: 1;
        }
        .icon {
            margin-left: 10px;
        }
        &.active {
            cursor: default;
        }
    }
    &.blue {
        .tab {
            @include tg-l($w5, usePalette($p-cool-gray, $p-800));
            border-bottom-color: usePalette($p-cool-gray, $p-200);
            &.active {
                @include tg-l($w7, usePalette());
                border-bottom-color: usePalette();
            }
        }
    }
    &.yellow {
        .tab {
            @include tg-l($w5, usePalette($p-yellow, $p-700));
            border-bottom-color: usePalette($p-yellow, $p-200);
            &.active {
                @include tg-l($w7);
                border-bottom-color: black;
            }
        }
    }
    &.white {
        .tab {
            @include tg-l($w5, usePalette($p-blue, $p-200));
            border-bottom-color: usePalette($p-blue, $p-200);
            &.active {
                @include tg-l($w7, usePalette($p-yellow));
                border-bottom-color: usePalette($p-yellow);
            }
        }
    }
}
</style>
